import {
  CHART_LIMITS,
  HEX_COLOUR_RE,
  REQUIRED_PALETTE_KEYS,
  findNearestOnset,
  type BaseChart,
  type ChartValidationIssue,
  type ChartValidationResult,
} from './chart.js';
import { VARIANTS, type Surface } from './gameTypes.js';

export interface FlipPad {
  id: string;
  beat: number;
  surface: Surface;
  optional?: boolean;
}

export interface Hazard {
  id: string;
  startBeat: number;
  endBeat: number;
  surface: Surface;
  kind: 'spike' | 'laser' | 'wall';
}

export interface SectionMarker {
  startBeat: number;
  endBeat: number;
  kind: import('./gameTypes.js').SectionKind;
  metadata?: Record<string, unknown>;
}

/**
 * "Mash" filler that occupies a stretch of song where there are no
 * required flip pads. Inside the chamber, every tap awards bonus score
 * (no combo effect, no HP penalty for "empty" taps) and the renderer
 * draws a pulsing MASH! overlay so the player knows to spam. Bursts
 * exist to keep the player engaged through long musical passages that
 * would otherwise feel dead - intros, breakdowns, riser tails, etc.
 *
 * Authoring rules:
 *  - 0 <= startBeat < endBeat <= chart.totalBeats
 *  - No required pad may sit inside [startBeat, endBeat] (validated)
 *  - Chambers may not overlap each other
 */
export interface BurstChamber {
  id: string;
  startBeat: number;
  endBeat: number;
}

/**
 * Pulse-mode chart payload. The original gravity-flip rhythm runner lives
 * here; pads on alternating surfaces are the player-facing primitive,
 * hazards/sections/burst chambers shape the rest of the song.
 */
export interface PulseChart extends BaseChart {
  mode: 'pulse';
  spawnSurface: Surface;
  pads: FlipPad[];
  hazards: Hazard[];
  sections: SectionMarker[];
  /**
   * Mash zones (see {@link BurstChamber}). Optional - older charts and
   * the builder route may omit it. Engine and renderer treat a missing
   * field as `[]`.
   */
  burstChambers?: BurstChamber[];
}

/**
 * Minimum span of a burst chamber in beats. Anything shorter feels like a
 * single tap window, not a "mash" - the renderer's MASH! overlay would
 * also barely have time to fade in. 2 beats ≈ 0.7s at 174 BPM.
 */
export const MIN_BURST_CHAMBER_BEATS = 2;

/**
 * Minimum gap (in beats) between a required pad and the nearest burst
 * chamber edge. The engine routes any tap inside a chamber to the mash
 * path, so a pad whose good window overlaps a chamber would be silently
 * un-hittable. 1 beat is comfortably > the 80ms good window even at
 * the fastest builtin BPM (174 → 80ms ≈ 0.23 beats).
 */
export const MIN_PAD_TO_BURST_BUFFER_BEATS = 1;

/**
 * Authoring rule: every required (non-optional) pad must sit within this
 * many milliseconds of an actual audio onset. Anything looser breaks the
 * "you tap when you hear something" promise.
 */
export const PAD_ONSET_TOLERANCE_MS = 25;

/**
 * Find the burst chamber containing `beat`, or null. Range is
 * half-open [startBeat, endBeat) to match the engine's expiry rule -
 * a tap exactly at endBeat falls outside the chamber and any
 * completion bonus has already been awarded by `expireFinishedBursts`.
 * Validation rejects overlapping chambers, so the first match wins.
 */
export function findActiveBurstChamber(
  chambers: ReadonlyArray<BurstChamber> | undefined,
  beat: number,
): BurstChamber | null {
  if (!chambers) {
    return null;
  }
  for (const c of chambers) {
    if (beat >= c.startBeat && beat < c.endBeat) {
      return c;
    }
  }
  return null;
}

export function validatePulseChart(chart: PulseChart): ChartValidationResult {
  const issues: ChartValidationIssue[] = [];

  validateBaseFields(chart, issues);

  if (chart.pads.length > CHART_LIMITS.maxPads) {
    issues.push({
      level: 'error',
      message: `pads ${chart.pads.length} exceeds limit ${CHART_LIMITS.maxPads}`,
    });
  }
  if (chart.hazards.length > CHART_LIMITS.maxHazards) {
    issues.push({
      level: 'error',
      message: `hazards ${chart.hazards.length} exceeds limit ${CHART_LIMITS.maxHazards}`,
    });
  }
  if (chart.sections.length > CHART_LIMITS.maxSections) {
    issues.push({
      level: 'error',
      message: `sections ${chart.sections.length} exceeds limit ${CHART_LIMITS.maxSections}`,
    });
  }
  const bursts = chart.burstChambers ?? [];
  if (bursts.length > CHART_LIMITS.maxBurstChambers) {
    issues.push({
      level: 'error',
      message: `burstChambers ${bursts.length} exceeds limit ${CHART_LIMITS.maxBurstChambers}`,
    });
  }

  if (chart.pads.length === 0) {
    issues.push({ level: 'error', message: 'Chart needs at least one pad' });
  } else if (chart.pads.every((p) => p.optional)) {
    // Charts where every pad is optional auto-complete with zero player
    // interaction. That's almost always a builder mistake, never a desired
    // map shape - flag it as an error so the player can't publish it.
    issues.push({
      level: 'error',
      message: 'Chart needs at least one required (non-optional) pad',
    });
  }

  validatePads(chart, issues);
  validateBurstChambers(chart, bursts, issues);

  return {
    ok: !issues.some((i) => i.level === 'error'),
    issues,
  };
}

function validateBaseFields(chart: PulseChart, issues: ChartValidationIssue[]): void {
  if (!chart.id) {
    issues.push({ level: 'error', message: 'Chart id is required' });
  }
  const title = chart.title ?? '';
  if (!title.trim()) {
    issues.push({ level: 'error', message: 'Chart title is required' });
  }
  if (title.length > CHART_LIMITS.maxTitleLength) {
    issues.push({
      level: 'error',
      message: `Title exceeds ${CHART_LIMITS.maxTitleLength} chars`,
    });
  }
  if ((chart.description ?? '').length > CHART_LIMITS.maxDescriptionLength) {
    issues.push({
      level: 'error',
      message: `Description exceeds ${CHART_LIMITS.maxDescriptionLength} chars`,
    });
  }
  if (chart.mapper !== undefined) {
    if (typeof chart.mapper !== 'string' || chart.mapper.trim().length === 0) {
      issues.push({
        level: 'error',
        message: 'mapper, when present, must be a non-empty string',
      });
    } else if (chart.mapper.length > CHART_LIMITS.maxMapperLength) {
      issues.push({
        level: 'error',
        message: `Mapper exceeds ${CHART_LIMITS.maxMapperLength} chars`,
      });
    }
  }
  if (!Number.isFinite(chart.bpm) || chart.bpm < 40 || chart.bpm > 500) {
    issues.push({
      level: 'error',
      message: `BPM ${chart.bpm} out of range (40-500)`,
    });
  }
  if (!Number.isFinite(chart.totalBeats) || chart.totalBeats <= 0) {
    issues.push({ level: 'error', message: 'totalBeats must be > 0' });
  }
  if (Number.isFinite(chart.totalBeats) && chart.totalBeats > CHART_LIMITS.maxTotalBeats) {
    issues.push({
      level: 'error',
      message: `totalBeats ${chart.totalBeats} exceeds limit ${CHART_LIMITS.maxTotalBeats}`,
    });
  }
  if (
    !Number.isInteger(chart.difficulty) ||
    chart.difficulty < 1 ||
    chart.difficulty > 5
  ) {
    issues.push({
      level: 'error',
      message: `difficulty ${chart.difficulty} must be integer 1-5`,
    });
  }
  if (!(VARIANTS as ReadonlyArray<string>).includes(chart.variant)) {
    issues.push({
      level: 'error',
      message: `variant ${chart.variant} must be one of ${VARIANTS.join(', ')}`,
    });
  }

  if (!chart.audio || typeof chart.audio.src !== 'string' || chart.audio.src.length === 0) {
    issues.push({ level: 'error', message: 'audio.src is required' });
  } else {
    if (!Number.isFinite(chart.audio.bpm) || chart.audio.bpm !== chart.bpm) {
      issues.push({
        level: 'error',
        message: `audio.bpm (${chart.audio.bpm}) must match chart.bpm (${chart.bpm})`,
      });
    }
    if (!Number.isFinite(chart.audio.offsetMs) || chart.audio.offsetMs < 0) {
      issues.push({ level: 'error', message: 'audio.offsetMs must be a non-negative number' });
    }
    if (!Number.isFinite(chart.audio.durationSec) || chart.audio.durationSec <= 0) {
      issues.push({ level: 'error', message: 'audio.durationSec must be > 0' });
    }
    if ((chart.audio.onsets?.length ?? 0) > CHART_LIMITS.maxOnsets) {
      issues.push({
        level: 'error',
        message: `audio.onsets ${chart.audio.onsets.length} exceeds limit ${CHART_LIMITS.maxOnsets}`,
      });
    }
  }

  for (const key of REQUIRED_PALETTE_KEYS) {
    const value = chart.palette?.[key];
    if (typeof value !== 'string' || !HEX_COLOUR_RE.test(value)) {
      issues.push({
        level: 'error',
        message: `palette.${key} must be a hex colour like #RRGGBB`,
      });
    }
  }
}

function validatePads(chart: PulseChart, issues: ChartValidationIssue[]): void {
  const padIds = new Set<string>();
  let prevBeat = -Infinity;
  let prevSurface: Surface = chart.spawnSurface;

  const sortedPads = [...chart.pads].sort((a, b) => a.beat - b.beat);
  const beatsPerMs =
    Number.isFinite(chart.bpm) && chart.bpm > 0 ? chart.bpm / 60_000 : 0;
  const onsetToleranceBeats = PAD_ONSET_TOLERANCE_MS * beatsPerMs;
  const hasOnsets = (chart.audio?.onsets?.length ?? 0) > 0;

  for (const pad of sortedPads) {
    if (padIds.has(pad.id)) {
      issues.push({ level: 'error', message: `Duplicate pad id ${pad.id}`, padId: pad.id });
    }
    padIds.add(pad.id);

    if (
      !Number.isFinite(pad.beat) ||
      pad.beat < 0 ||
      pad.beat > chart.totalBeats
    ) {
      issues.push({
        level: 'error',
        message: `Pad ${pad.id} at beat ${pad.beat} outside [0, ${chart.totalBeats}]`,
        beat: pad.beat,
        padId: pad.id,
      });
    }

    if (!pad.optional && pad.surface === prevSurface && pad.beat > prevBeat) {
      // Every required hit must alternate surface from the previous one
      // (or spawnSurface for the first pad). Same-surface required pads
      // would mean the player has to "tap on the opposite rail" without
      // anything visibly happening - the user complained about exactly
      // this in the UX overhaul.
      issues.push({
        level: 'error',
        message: `Pad ${pad.id} at beat ${pad.beat} on same surface as previous required pad - flips must alternate floor and ceiling.`,
        beat: pad.beat,
        padId: pad.id,
      });
    }

    if (
      !pad.optional &&
      hasOnsets &&
      Number.isFinite(pad.beat)
    ) {
      const nearest = findNearestOnset(chart.audio, pad.beat);
      if (nearest && Math.abs(nearest.beat - pad.beat) > onsetToleranceBeats) {
        issues.push({
          level: 'error',
          message: `Pad ${pad.id} at beat ${pad.beat.toFixed(2)} is more than ${PAD_ONSET_TOLERANCE_MS}ms from the nearest audio onset (beat ${nearest.beat.toFixed(2)}). Move it onto a beat the player can hear.`,
          beat: pad.beat,
          padId: pad.id,
        });
      }
    }

    if (!pad.optional) {
      prevBeat = pad.beat;
      prevSurface = pad.surface;
    }
  }
}

function validateBurstChambers(
  chart: PulseChart,
  bursts: ReadonlyArray<BurstChamber>,
  issues: ChartValidationIssue[],
): void {
  const seenIds = new Set<string>();
  // Track which chambers passed the structural checks so the overlap
  // pass below can ignore malformed entries (a chamber with NaN endBeat
  // would let any neighbour through `prev.endBeat > burst.startBeat`).
  const valid: BurstChamber[] = [];
  for (const burst of [...bursts].sort((a, b) => a.startBeat - b.startBeat)) {
    if (seenIds.has(burst.id)) {
      issues.push({ level: 'error', message: `Duplicate burstChamber id ${burst.id}` });
    }
    seenIds.add(burst.id);

    if (
      !Number.isFinite(burst.startBeat) ||
      !Number.isFinite(burst.endBeat) ||
      burst.startBeat < 0 ||
      burst.endBeat > chart.totalBeats ||
      burst.endBeat - burst.startBeat < MIN_BURST_CHAMBER_BEATS
    ) {
      issues.push({
        level: 'error',
        message: `BurstChamber ${burst.id} [${burst.startBeat}, ${burst.endBeat}] must lie within [0, ${chart.totalBeats}] and span at least ${MIN_BURST_CHAMBER_BEATS} beats`,
      });
      continue;
    }

    const prev = valid[valid.length - 1];
    if (prev && prev.endBeat > burst.startBeat) {
      issues.push({
        level: 'error',
        message: `BurstChamber ${burst.id} overlaps ${prev.id} (${prev.startBeat}-${prev.endBeat} vs ${burst.startBeat}-${burst.endBeat})`,
      });
    }
    valid.push(burst);

    for (const pad of chart.pads) {
      if (!Number.isFinite(pad.beat)) {
        continue;
      }
      // Half-open [start, end) - matches the engine's findActiveBurst /
      // expireFinishedBursts ranges so authoring rules and runtime
      // behaviour can't drift.
      const inside = pad.beat >= burst.startBeat && pad.beat < burst.endBeat;
      if (inside) {
        issues.push({
          level: 'error',
          message: `Pad ${pad.id} at beat ${pad.beat} sits inside burst chamber ${burst.id} (${burst.startBeat}-${burst.endBeat}). Burst chambers must contain no pads (chambers swallow taps before pad resolution).`,
          beat: pad.beat,
          padId: pad.id,
        });
        continue;
      }
      if (pad.optional) {
        continue;
      }
      // Required pads also need a small buffer outside the chamber so
      // their good window doesn't overlap the chamber range.
      const distToStart = burst.startBeat - pad.beat;
      const distToEnd = pad.beat - burst.endBeat;
      if (
        (distToStart > 0 && distToStart < MIN_PAD_TO_BURST_BUFFER_BEATS) ||
        (distToEnd > 0 && distToEnd < MIN_PAD_TO_BURST_BUFFER_BEATS)
      ) {
        issues.push({
          level: 'error',
          message: `Pad ${pad.id} at beat ${pad.beat} sits within ${MIN_PAD_TO_BURST_BUFFER_BEATS} beats of burst chamber ${burst.id} (${burst.startBeat}-${burst.endBeat}). Its hit window would overlap the chamber and become unhittable.`,
          beat: pad.beat,
          padId: pad.id,
        });
      }
    }
  }
}
